<template>
  <div class="cms-custom-code-runtime" :class="{ 'is-hydrating': isHydrating }">
    <div ref="hostEl" class="cms-custom-code-runtime__host" />
    <div v-if="!mounted" class="cms-custom-code-runtime__ssr">
      <div v-html="ssrHtml" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import * as VueRuntime from 'vue/dist/vue.esm-bundler.js'

const props = defineProps<{
  html: string
  css: string
  js: string
  vars: Record<string, unknown>
}>()

const hostEl = ref<HTMLElement | null>(null)
const mounted = ref(false)
const isHydrating = ref(true)

const tailwindCssCache = new Map<string, string>()
const TOP_LEVEL_DECL_RE = /(?:^|[\r\n])\s*(?:const|let|var|function|class)\s+([A-Za-z_$][\w$]*)/g

let runtimeApp: ReturnType<typeof VueRuntime.createApp> | null = null
let mountEl: HTMLDivElement | null = null
let userStyleEl: HTMLStyleElement | null = null
let tailwindStyleEl: HTMLStyleElement | null = null
let renderVersion = 0

const templateHtml = computed(() => (props.html || '').trim() || '<div></div>')
const ssrHtml = computed(() => {
  const html = templateHtml.value
  const css = String(props.css || '').trim()
  if (!css) return html
  const safeCss = css.replace(/<\/style/gi, '<\\/style')
  return `<style>${safeCss}</style>${html}`
})

const extractBindings = (code: string) => {
  const names = new Set<string>()
  let match: RegExpExecArray | null
  TOP_LEVEL_DECL_RE.lastIndex = 0
  while ((match = TOP_LEVEL_DECL_RE.exec(code)) !== null) {
    names.add(match[1])
  }
  return [...names]
}

const mountError = (message: string) => {
  if (!mountEl) return
  mountEl.innerHTML = ''
  const box = document.createElement('pre')
  box.className = 'cms-custom-code-runtime__error'
  box.textContent = message
  mountEl.appendChild(box)
}

const unmountRuntime = () => {
  if (runtimeApp) {
    try {
      runtimeApp.unmount()
    } catch {}
    runtimeApp = null
  }
  if (hostEl.value) {
    hostEl.value.innerHTML = ''
  }
  mountEl = null
  userStyleEl = null
  tailwindStyleEl = null
}

const getTailwindCss = async (html: string) => {
  const key = html.trim()
  if (!key) return ''
  const cached = tailwindCssCache.get(key)
  if (cached != null) return cached
  try {
    const response = await $fetch<{ css?: string }>('/api/cms/custom-tailwind', {
      method: 'POST',
      body: { html },
    })
    const css = response?.css || ''
    tailwindCssCache.set(key, css)
    return css
  } catch (error) {
    console.warn('[CmsCustomCodeRuntime] tailwind compile failed:', error)
    tailwindCssCache.set(key, '')
    return ''
  }
}

const createUserBindings = () => {
  const code = props.js || ''
  if (!code.trim()) return {}
  const exposed = extractBindings(code)
  const exposureCode = exposed.length
    ? `return {
${exposed.map((name) => `  ${JSON.stringify(name)}: typeof ${name} !== 'undefined' ? ${name} : undefined`).join(',\n')}
}`
    : 'return {}'
  const runner = new Function(
    'context',
    'Vue',
    `"use strict";
const { ref, reactive, shallowReactive, readonly, computed, watch, watchEffect, nextTick, onMounted, onUnmounted, onBeforeMount, onBeforeUnmount } = Vue;
const vars = context.vars;
const cmsVars = context.vars;
const container = context.container;
const host = context.host;
${code}
${exposureCode}
`,
  )
  const result = runner({
    vars: props.vars,
    container: mountEl,
    host: hostEl.value,
  }, VueRuntime)
  return result && typeof result === 'object' ? result as Record<string, unknown> : {}
}

const mountRuntime = () => {
  if (!mountEl) return
  const component = VueRuntime.defineComponent({
    name: 'CmsCustomCodeRuntimeInner',
    template: templateHtml.value,
    setup() {
      try {
        return {
          ...props.vars,
          ...createUserBindings(),
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        queueMicrotask(() => mountError(`[CmsCustomCodeRuntime] JavaScript error\n${message}`))
        return { ...props.vars }
      }
    },
  })
  runtimeApp = VueRuntime.createApp(component)
  runtimeApp.config.warnHandler = (msg: unknown) => {
    console.warn('[CmsCustomCodeRuntime] Vue warn:', msg)
  }
  runtimeApp.config.errorHandler = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error)
    mountError(`[CmsCustomCodeRuntime] Vue render error\n${message}`)
  }
  try {
    runtimeApp.mount(mountEl)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    mountError(`[CmsCustomCodeRuntime] Template error\n${message}`)
  }
}

const renderRuntime = async () => {
  const host = hostEl.value
  if (!host) return
  const currentVersion = ++renderVersion
  unmountRuntime()
  
  // SSR removal and transition to Light DOM
  mounted.value = true
  isHydrating.value = false

  userStyleEl = document.createElement('style')
  userStyleEl.textContent = props.css || ''
  
  tailwindStyleEl = document.createElement('style')
  
  mountEl = document.createElement('div')
  mountEl.className = 'cms-custom-code-runtime__app'
  
  host.append(tailwindStyleEl, userStyleEl, mountEl)
  tailwindStyleEl.textContent = await getTailwindCss(templateHtml.value)
  
  if (currentVersion !== renderVersion) return
  mountRuntime()
}

onMounted(() => {
  renderRuntime()
})

watch(
  () => [props.html, props.css, props.js, props.vars],
  () => {
    if (mounted.value) {
      renderRuntime()
    }
  },
  { deep: true },
)

onUnmounted(() => {
  unmountRuntime()
})
</script>

<style>
.cms-custom-code-runtime__error {
  margin: 0;
  padding: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  white-space: pre-wrap;
  font: 12px/1.6 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.cms-custom-code-runtime {
  display: contents;
}
.cms-custom-code-runtime__ssr {
  display: contents;
}
.cms-custom-code-runtime__host {
  display: contents;
}
</style>

<style scoped>
.cms-custom-code-runtime {
  width: 100%;
  display: block;
}
</style>
